require('vue-moment')
import {mapGetters, mapActions} from 'vuex'

// A healthy hub handshake is sub-second, so once it starts we cap it short to detect a
// frame-dropping blackhole fast. The overall cap is a generous backstop for a slow (but real)
// negotiate/connect that hangs before the handshake. Both tunable via env.
const SIGNALR_HANDSHAKE_TIMEOUT_MS = parseInt(process.env.VUE_APP_SIGNALR_HANDSHAKE_TIMEOUT_MS, 10) || 12000
const SIGNALR_OVERALL_TIMEOUT_MS = parseInt(process.env.VUE_APP_SIGNALR_OVERALL_TIMEOUT_MS, 10) || 25000

// When a transport blackhole is confirmed (handshake hung, a lower transport then worked), remember
// the rung that worked so future connections start there and skip the handshake-cap wait. Self-expires
// after the TTL, then re-probes from the top. The legacy WebSocket-only key is honoured for migration;
// the TTL env var name is kept so existing environment configs still apply.
const SIGNALR_TRANSPORT_FLOOR_KEY = 'signalr-transport-floor'
const SIGNALR_LEGACY_SKIP_WS_KEY = 'signalr-skip-websockets-until'
const SIGNALR_TRANSPORT_FLOOR_TTL_MS = (parseInt(process.env.VUE_APP_SIGNALR_SKIP_WS_HOURS, 10) || 24) * 60 * 60 * 1000

// Diagnostics are always captured to the store (and copyable from the failure modal); this only
// gates the console echo. On in all environments for now; set VUE_APP_SIGNALR_CONSOLE_LOG="false" to silence.
const SIGNALR_CONSOLE_LOG = process.env.VUE_APP_SIGNALR_CONSOLE_LOG !== 'false'

// Shared between the per-message classifier and the whole-walk classifier (classifyLadderFailure).
const RT_003_BLOCKED = { code: 'RT-003', summary: 'The connection was blocked on every available method. This is usually caused by a proxy or firewall.' }
const RT_005_BLACKHOLED = { code: 'RT-005', summary: 'The connection was established but then silently blocked. This is usually caused by a proxy or firewall.' }

export default {
    computed: {
        ...mapGetters({
            signalRClientId: 'SignalRStore/GetClientId',
            token: 'SignalRStore/GetToken',
            tokenDateCreated: 'SignalRStore/GetTokenDateCreated',
            tokenDateExpiry: 'SignalRStore/GetTokenDateExpiry',
            tokenExpirySeconds: 'SignalRStore/GetTokenExpirySeconds',
            signalRConnection: 'SignalRStore/GetConnection',
            tokenResponse: 'SignalRStore/GetTokenResponse',
            timerInterval: 'SignalRStore/GetTimerInterval',
            connectionDiagnostics: 'SignalRStore/GetConnectionDiagnostics',
            connectionError: 'SignalRStore/GetConnectionError'
        })
    },

    watch: {
        // RT-006 is set by handleUnexpectedConnectionClose, outside any user action, so no view
        // has a promise to catch. A view that needs to react (modal, redirect, error state)
        // defines onSignalRConnectionLost; views without one ignore the drop.
        connectionError(error) {
            if(error && error.code === 'RT-006' && typeof this.onSignalRConnectionLost === 'function') {
                this.onSignalRConnectionLost(error)
            }
        }
    },

    methods: {
        ...mapActions({
            setClientIdInSignalRStore: 'SignalRStore/SetClientId',
            setTokenInSignalRStore: 'SignalRStore/SetToken',
            setTokenDateCreatedInSignalRStore: 'SignalRStore/SetTokenDateCreated',
            setTokenDateExpiryInSignalRStore: 'SignalRStore/SetTokenDateExpiry',
            setConnectionInSignalRStore: 'SignalRStore/SetConnection',
            setTimerIntervalInSignalRStore: 'SignalRStore/SetTimerInterval',
            setTokenExpirySeconds: 'SignalRStore/SetTokenExpirySeconds',
            setTokenResponse: 'SignalRStore/SetTokenResponse',
            clearConnectionInSignalRStore: 'SignalRStore/ClearConnection',
            addConnectionDiagnostic: 'SignalRStore/AddConnectionDiagnostic',
            clearConnectionDiagnostics: 'SignalRStore/ClearConnectionDiagnostics',
            setConnectionError: 'SignalRStore/SetConnectionError',
            setActiveTransport: 'SignalRStore/SetActiveTransport'
        }),

        recordConnectionDiagnostic(level, message) {
            if(SIGNALR_CONSOLE_LOG) console.log(`[SignalR ${level}] ${message}`)
            this.addConnectionDiagnostic({
                at: new Date().toISOString(),
                level,
                message
            })
        },

        commitActiveTransport(transportName) {
            const transports = {
                WebSockets: { code: 'CON-001', name: 'WebSockets' },
                ServerSentEvents: { code: 'CON-002', name: 'Server-Sent Events' },
                LongPolling: { code: 'CON-003', name: 'Long Polling' }
            }
            const transport = transports[transportName] || { code: 'CON-000', name: transportName || 'Unknown' }
            if(SIGNALR_CONSOLE_LOG) console.info(`[SignalR] Connected via ${transport.name} (${transport.code})`)
            this.setActiveTransport(transport)
        },

        classifyConnectionError(error) {
            const message = ((error && error.message) || String(error || '')).toLowerCase()

            // RT-001: service temporarily unavailable (negotiate 500 / app server not yet connected to Azure SignalR)
            if(message.includes('not connected yet') || message.includes("status code '500'")) {
                return { code: 'RT-001', summary: 'The service was temporarily unavailable. Please wait a few minutes and try again.' }
            }
            // RT-002: cannot reach the messaging service (network/firewall blocking the host)
            if(message.includes('failed to fetch')) {
                return { code: 'RT-002', summary: 'We couldn\'t reach the messaging service. This is usually caused by a network or firewall restriction.' }
            }
            // RT-003: transport(s) refused at connect (firewall blocking the connections outright)
            if(message.includes('unable to connect to the server with any of the available transports')) {
                return RT_003_BLOCKED
            }
            // RT-005: a transport opened but the hub handshake never completed (frames silently dropped, then capped)
            if(message.includes('handshake') || message.includes('timed out waiting for hub handshake')) {
                return RT_005_BLACKHOLED
            }
            // RT-004: unclassified connection failure
            return { code: 'RT-004', summary: 'An unexpected connection error occurred.' }
        },

        // The ladder's walk is the truth, not the last rung's error message: WS hanging then
        // SSE/LP being refused would otherwise read as "never opened" (RT-003, allowlist triage),
        // and refusals ending in an open-then-error would read as unclassified (RT-004). Any
        // blackhole anywhere on the walk means a deep-inspection proxy is in play (RT-005);
        // failing that, any refusal means RT-003; only then is the last error's text trusted.
        classifyLadderFailure(lastError, sawBlackhole, sawRefusal) {
            if(sawBlackhole) return RT_005_BLACKHOLED
            if(sawRefusal) return RT_003_BLOCKED
            return this.classifyConnectionError(lastError)
        },

        signalRDiagnosticsLogger() {
            const levelNames = ['Trace', 'Debug', 'Information', 'Warning', 'Error', 'Critical', 'None']
            return {
                log: (logLevel, message) => {
                    if(logLevel <= signalR.LogLevel.Trace) return // skip Trace: it logs message payloads (patient data)
                    const level = levelNames[logLevel] || String(logLevel)
                    // The handshake is the last step of start(); arm the short handshake cap once it begins.
                    if(message && message.indexOf('Sending handshake request') !== -1 && this._armHandshakeTimeout) {
                        this._armHandshakeTimeout()
                    }
                    // Track the winning transport: the last 'Selecting transport' before the hub connects.
                    const transportMatch = message && message.match(/Selecting transport '(\w+)'/)
                    if(transportMatch) this._candidateTransport = transportMatch[1]
                    if(message && message.indexOf('HubConnection connected successfully') !== -1) {
                        this.commitActiveTransport(this._candidateTransport)
                    }
                    this.recordConnectionDiagnostic(level, message) // console output happens in recordConnectionDiagnostic
                }
            }
        },

        // The ladder: exactly ONE transport per rung, so the library's internal fallback can
        // never run — every WS -> SSE -> LongPolling hop is made here, with fresh timers, a
        // diagnostic line, and floor caching. (SignalR commits to any transport the moment it
        // connects and never falls back if the handshake then hangs or errors; its internal
        // connect-time hops also share one attempt's timers and bypass our cache. Single-
        // transport rungs remove both behaviours.)
        // A rung's `options` are passed through to withUrl, so per-transport connection settings
        // live on the rung they apply to.
        transportRungs() {
            return [
                { name: 'WebSockets only', transport: signalR.HttpTransportType.WebSockets },
                { name: 'SSE only', transport: signalR.HttpTransportType.ServerSentEvents },
                // skipNegotiation must stay false: negotiation is how the client reaches Azure
                // SignalR at all, and LongPolling cannot skip it. Stated explicitly on the rung
                // so the requirement is visible and survives future option changes.
                { name: 'LongPolling only', transport: signalR.HttpTransportType.LongPolling, options: { skipNegotiation: false } }
            ]
        },

        getCachedTransportFloor() {
            const maxRung = this.transportRungs().length - 1
            try {
                const cached = JSON.parse(window.localStorage.getItem(SIGNALR_TRANSPORT_FLOOR_KEY))
                // rung 0 is "no floor" — a stored 0 is malformed, not a floor, so it must not pass
                const isValidFloor = cached && cached.rung >= 1 && cached.until && Date.now() < cached.until
                if(isValidFloor) return Math.min(cached.rung, maxRung)
            } catch { /* unreadable cache treated as empty */ }
            try {
                const legacyUntil = parseInt(window.localStorage.getItem(SIGNALR_LEGACY_SKIP_WS_KEY), 10)
                if(Boolean(legacyUntil) && Date.now() < legacyUntil) return 1
            } catch { /* storage unavailable (private mode / quota) */ }
            return 0
        },

        rememberTransportFloor(rung) {
            try {
                window.localStorage.setItem(SIGNALR_TRANSPORT_FLOOR_KEY, JSON.stringify({ rung, until: Date.now() + SIGNALR_TRANSPORT_FLOOR_TTL_MS }))
                window.localStorage.removeItem(SIGNALR_LEGACY_SKIP_WS_KEY)
            } catch { /* storage unavailable */ }
        },

        clearTransportFloor() {
            try {
                window.localStorage.removeItem(SIGNALR_TRANSPORT_FLOOR_KEY)
                window.localStorage.removeItem(SIGNALR_LEGACY_SKIP_WS_KEY)
            } catch { /* storage unavailable */ }
        },

        hubUrl() {
            return `${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/diagnosticsHub`
        },

        buildHubConnection(rung) {
            return new signalR.HubConnectionBuilder()
                .configureLogging(this.signalRDiagnosticsLogger())
                .withUrl(this.hubUrl(), { transport: rung.transport, ...rung.options })
                .build()
        },

        async startWithTimeout(connection, overallMs, handshakeMs) {
            var overallHandle = null
            var handshakeHandle = null
            var rejectGuard = null

            const guardPromise = new Promise((resolve, reject) => {
                rejectGuard = reject
                // Backstop: a negotiate/connect that hangs before the handshake (e.g. host unreachable).
                overallHandle = setTimeout(() => {
                    const error = new Error(`Connection timed out after ${overallMs}ms`)
                    error.overallTimedOut = true
                    reject(error)
                }, overallMs)
            })

            // Armed by the logger when the handshake begins. Because it only arms after the handshake
            // starts, a short cap here detects a frame-dropping blackhole fast without ever cutting off
            // a legitimately slow negotiate.
            this._armHandshakeTimeout = () => {
                if(handshakeHandle != null) return
                handshakeHandle = setTimeout(() => {
                    const error = new Error(`Timed out waiting for hub handshake after ${handshakeMs}ms`)
                    error.handshakeTimedOut = true
                    rejectGuard(error)
                }, handshakeMs)
            }

            const startPromise = connection.start()
            startPromise.catch(() => {}) // swallow a late rejection if a timeout wins the race

            try {
                await Promise.race([startPromise, guardPromise])
            } finally {
                clearTimeout(overallHandle)
                clearTimeout(handshakeHandle)
                this._armHandshakeTimeout = null
            }
        },

        async openSignalRConnection() {
            try {
                this.closeSignalRConnection() // in case anything is already open
                this.clearConnectionDiagnostics()
                this.setConnectionError(null) // a stale code from an earlier attempt must not outlive it

                this.recordConnectionDiagnostic('Information', `Starting connection to ${this.hubUrl()}`)

                const rungs = this.transportRungs()
                const startRung = this.getCachedTransportFloor()
                let rung = startRung
                let attempt = 0
                let sawBlackhole = false // a transport opened (or was selected) and then died
                let sawRefusal = false // a transport was refused at connect

                while(rung < rungs.length) {
                    attempt++
                    this._candidateTransport = null
                    const connection = this.buildHubConnection(rungs[rung])
                    try {
                        this.recordConnectionDiagnostic('Information', `Attempt ${attempt}: ${rungs[rung].name}${rung === startRung && startRung > 0 ? ' (recent transport blackhole cached)' : ''}`)
                        await this.startWithTimeout(connection, SIGNALR_OVERALL_TIMEOUT_MS, SIGNALR_HANDSHAKE_TIMEOUT_MS)
                        this.recordConnectionDiagnostic('Information', `Connected on attempt ${attempt} (${rungs[rung].name}). connectionId: ${connection.connectionId}`)
                        if(rung > startRung) {
                            // Confirmed blackhole: a higher transport hung, this rung works. Cache it so
                            // future connections start here, until the cache expires and re-probes.
                            this.rememberTransportFloor(rung)
                            this.recordConnectionDiagnostic('Information', `Cached transport blackhole; future connections will start at ${rungs[rung].name}`)
                        }
                        connection.onclose(error => this.handleUnexpectedConnectionClose(connection, error))
                        this.setConnectionInSignalRStore(connection)
                        this.setClientIdInSignalRStore(connection.connectionId)
                        return
                    } catch (e) {
                        const classified = this.classifyConnectionError(e)
                        this.recordConnectionDiagnostic('Warning', `Attempt ${attempt} failed [${classified.code}]: ${(e && e.message) || e}`)
                        try { await connection.stop() } catch { /* release the failed socket before retrying */ }

                        // Each rung allows exactly one transport, so any failure that implicates the
                        // transport descends to the next rung: a handshake hang, any error after the
                        // transport was selected, or the library's "with any of the available
                        // transports" aggregate — which under a single-transport rung fires on a plain
                        // connect refusal and can only mean THIS rung's transport (unlike the old
                        // multi-transport rungs, where it meant everything below was already tried).
                        // Only failures before any transport was selected (negotiate/network level) are
                        // terminal: every rung negotiates against the same endpoint, so a retry below
                        // cannot help.
                        const failedMessage = ((e && e.message) || '').toLowerCase()
                        const transportRefused = failedMessage.indexOf('with any of the available transports') !== -1
                        const hung = Boolean(e && e.handshakeTimedOut)
                        const committed = this._candidateTransport
                        if(hung || (committed && !transportRefused)) sawBlackhole = true
                        else if(transportRefused) sawRefusal = true
                        const nextRung = (hung || committed || transportRefused) ? rung + 1 : rungs.length

                        if(nextRung >= rungs.length) {
                            // If we started from a cached floor, the cached assumption may be stale
                            // (network changed) — clear it so the next connect re-probes from the top.
                            if(startRung > 0) this.clearTransportFloor()
                            this.setConnectionError(this.classifyLadderFailure(e, sawBlackhole, sawRefusal))
                            throw new Error()
                        }
                        this.recordConnectionDiagnostic('Information', `Attempt ${attempt} ${hung ? 'hung at' : 'failed after committing to'} ${committed || rungs[rung].name}; retrying with ${rungs[nextRung].name}`)
                        rung = nextRung
                    }
                }
            } catch {
                this.closeSignalRConnection()
                throw new Error()
            }
        },

        // Fires only when the ACTIVE connection dies on its own (network drop, proxy cutting the
        // socket, service restart). App-initiated stops — closeSignalRConnection, the router's
        // navigation cleanup, a superseding openSignalRConnection — clear or replace the store
        // reference before the callback runs, so the identity check filters them out.
        // No automatic reconnect happens here: the user retries the step (see RT-006).
        handleUnexpectedConnectionClose(connection, error) {
            if(this.signalRConnection !== connection) return
            this.recordConnectionDiagnostic('Warning', `Connection closed unexpectedly${error ? `: ${error.message || error}` : ''}`)
            this.stopTokenExpiryCountdown()
            this.clearConnectionInSignalRStore()
            this.setConnectionError({
                code: 'RT-006',
                summary: 'The live connection was lost after it had been established. Please retry the current step. This can be caused by a network interruption or a proxy closing the connection.'
            })
        },

        closeSignalRConnection() {
            this.stopTokenExpiryCountdown()
            if(this.signalRConnection && Object.keys(this.signalRConnection).length) {
                this.signalRConnection.stop()
                this.clearConnectionInSignalRStore()
            }
        },

        isSignalRConnected() {
            if(this.token != null && this.signalRConnection != null && Object.keys(this.signalRConnection).length && this.signalRConnection.state === signalR.HubConnectionState.Connected) {
                return true
            }

            return false
        },

        getTokenExpiry() {
            if(this.token == null || this.signalRClientId == null || this.$moment(this.tokenDateExpiry).isBefore(this.$moment.utc())) return
            
            var interval = setInterval(() => {
                var tokenExpiry = this.$moment(this.tokenDateExpiry)
                var now = this.$moment.utc()

                var difference = tokenExpiry.diff(now, 'seconds')
                this.setTokenExpirySeconds(difference)
            }, 1000)
            
            this.setTimerIntervalInSignalRStore(interval)
        },

        stopTokenExpiryCountdown() {
            if(this.timerInterval != null) clearInterval(this.timerInterval)
        }
    }
}